import { type FormEvent, useCallback, useState } from "react";
import { useHotelSearch as useCoreHotelSearch } from "@b2bportal/core-hotels";
import type { InputErrorType } from "@b2bportal/core-types";
import type { Suggestion } from "@b2bportal/lodging-api";

type FormErrors = {
  place?: InputErrorType;
  dateRange?: InputErrorType;
};

export const useHotelSearch = () => {
  const { context, handlers } = useCoreHotelSearch();

  const [formError, setFormError] = useState<FormErrors>({});

  const handlePlaceChange = useCallback(
    (place: Suggestion) => {
      handlers.handlePlaceChange(place);
      setFormError((prev) => ({ ...prev, place: undefined }));
    },
    [handlers]
  );

  const handlePlaceFocus = useCallback(() => {
    setFormError((prev) => ({ ...prev, place: undefined }));
  }, []);

  const handleDateRangeFromChange = useCallback(
    (date: string | undefined) => {
      handlers.handleDateRangeFromChange(date);
      setFormError((prev) => ({ ...prev, dateRange: undefined }));
    },
    [handlers]
  );

  const handleDateRangeUntilChange = useCallback(
    (date: string | undefined) => {
      handlers.handleDateRangeUntilChange(date);
      setFormError((prev) => ({ ...prev, dateRange: undefined }));
    },
    [handlers]
  );

  const onFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!context.isFormValid) {
        const errors: FormErrors = {};
        if (!context.place) {
          errors.place = { message: undefined };
        }
        if (!context.dateRange?.from || !context.dateRange?.until) {
          errors.dateRange = { message: undefined };
        }
        setFormError(errors);
        return;
      }

      handlers.handleSearch();
    },
    [context.isFormValid, context.place, context.dateRange, handlers]
  );

  return {
    context: {
      ...context,
      formError,
    },
    handlers: {
      ...handlers,
      handlePlaceChange,
      handlePlaceFocus,
      handleDateRangeFromChange,
      handleDateRangeUntilChange,
      onFormSubmit,
    },
  };
};
